<template>
  <div class="custom-market">
    <text-input
      class="market-name"
      :value="marketName"
      @change="({ value }) => changeMarketName(value)"
    />
    <location-selection
      :value="location"
      @search="loadBroadcasts"
    />
    <broadcast-list v-if="broadcastsLoading || broadcastsInitialized" />
    <broadcast-list-placeholder v-else />
    <div class="market-actions">
      <div class="action-buttons">
        <button class="cancel-button" @click="cancel">
          Cancel
        </button>
        <button
          class="create-market-button"
          :disabled="!canSaveCustomMarket"
          @click="saveChanges"
        >
          {{ saveLabel }}
        </button>
      </div>
    </div>
    <market-name-modal ref="marketNameModal" />
  </div>
</template>

<script>
import {
  mapState, mapGetters, mapMutations, mapActions,
} from 'vuex';
import TextInput from '@/components/common/TextInput';
import BroadcastListPlaceholder from './_BroadcastListPlaceholder';
import BroadcastList from './_BroadcastList';
import LocationSelection from './_LocationSelection';
import MarketNameModal from './_MarketNameModal';

export default {
  name: 'CustomMarket',
  components: {
    TextInput,
    BroadcastListPlaceholder,
    BroadcastList,
    LocationSelection,
    MarketNameModal,
  },
  props: {
    saveLabel: {
      type: String,
      default: 'Create market',
    },
    market: {
      type: Object,
      default: null,
    },
  },
  computed: {
    ...mapState('setup', ['pendingMarketInput']),
    ...mapState('customMarket', ['location']),
    ...mapState('customMarket', {
      marketName: 'name',
      broadcastsLoading: 'loading',
    }),
    ...mapGetters('customMarket', {
      broadcastsInitialized: 'initialized',
    }),
    ...mapGetters('customMarket', ['canSaveCustomMarket', 'isNameSpecified']),
  },
  methods: {
    ...mapMutations('customMarket', {
      changeMarketName: 'setName',
    }),
    ...mapMutations('setup', ['selectMarket']),
    ...mapActions('customMarket', [
      'loadBroadcasts',
      'saveCustomMarket',
    ]),
    cancel() {
      this.$router.push({ name: 'setup' });
    },
    async saveChanges() {
      if (!this.isNameSpecified && !(await this.$refs.marketNameModal.confirm())) return;

      const customMarket = await this.saveCustomMarket();
      if (customMarket && this.pendingMarketInput !== null) {
        this.selectMarket({ market: customMarket, index: this.pendingMarketInput });
      }
      this.$router.push({ name: 'setup' });
    },
  },
};
</script>

<style scoped lang="scss">
@import "~@/assets/scss/variables";
@import "~@/assets/scss/buttons";
@import "~@/assets/scss/mixins";

$max-width: 1160px;
$min-width: $mobile;
$side-padding: 41px;
$action-buttons-height: 130px;
$action-buttons-height-tablet: 156px;

.custom-market {
  font-family: Lato;
  display: flex;
  flex-direction: column;
  margin: auto;

  box-sizing: border-box;
  max-width: $max-width;
  min-width: $min-width;
  padding: 44px $side-padding;

  @include until($tablet) {
    padding: 44px $side-padding / 2;
  }
}

.market-name {
  width: 410px;
  margin-bottom: 33px;
}

.location-selector {
  padding-bottom: 20px;
  @include until($tablet) {
    flex-direction: column;
  }
}

.broadcast-list {
  margin-bottom: $action-buttons-height;
  @include until($tablet) {
    margin-bottom: $action-buttons-height-tablet;
  }
}

.market-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: $action-buttons-height;
  max-width: $max-width;
  min-width: $min-width;
  padding: 0 $side-padding;
  margin: auto;
  box-sizing: border-box;
  background-color: $charcoal-nero;

  @include until($tablet) {
    height: $action-buttons-height-tablet;
    padding: 0 $side-padding / 2;
  }

  .action-buttons {
    display: flex;
    justify-content: space-between;

    padding: 40px 0;
    border-top: 1px solid #333333;

    @include until($tablet) {
      flex-direction: column;
      align-items: center;
      padding: 20px 0;
    }

    .cancel-button {
      @include button(#1E1D1D, #333333);
    }

    .create-market-button {
      @include button($orange, $orange, $white);
    }

    .cancel-button,
    .create-market-button {
      height: 50px;
      width: 240px;

      @include until($tablet) {
        width: 100%;
        margin-bottom: 16px;
      }
    }
  }
}
</style>
